import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import http from 'utils/http';
import qs from 'qs';
import { format } from 'date-fns/fp';
import Accordion from '@mui/material/Accordion';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import Stack from '@mui/material/Stack';
import { DrawerLayoutWrapper } from 'common';
import { useGetCarrierParams } from 'feature/dispatching/hooks';
import { useUserRolePermissions } from 'hooks';
import AccordionSummary from '../../components/AccordionSummary';
import StatusChip from '../components/StatusChip';
import { useViewLoadQuery } from '../loadsEndpoints';
import Item from './Item';
import CustomRecipe from './CustomRecipe';
import HouseRecipe from './HouseRecipe';
import Unload from './Unload';
import EmptyLoad from './EmptyLoad';

export const downloadFile = (billOfLadingIds, locationId, carrier) => {
  const { carrierId, magellanId } = carrier;
  const params = qs.stringify(
    { bol: billOfLadingIds, locationId },
    {
      allowDots: true,
      encode: false,
    }
  );
  http
    .get(
      `${process.env.REACT_APP_PRODUCT_LOADING_BASE_URL}/dispatching-carrier-authorizations/download-bol?${params}`,
      {
        headers: {
          'X-CARRIERID': carrierId,
          'X-MAGELLANID': magellanId,
        },
        responseType: 'blob',
      }
    )
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bol.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    });
};

const accordionStyles = {
  border: 1,
  borderColor: 'graphic.light',
  boxShadow: 'none',
  width: 390,
};

// propOrDashes :: String -> Object -> String
const propOrDashes = R.propOr('--');

// formatDateTime :: String -> String
const formatDate = format('P');

// formatDateTime :: String -> String
const formatDateTime = format('P p');

const ViewLoadDrawer = ({ loadId, onClose, onDelete, onDuplicate, onEdit }) => {
  const { carrier } = useGetCarrierParams();
  const { isDispatchingManage } = useUserRolePermissions();
  const { data: loadQuery = {}, isFetching, isSuccess } = useViewLoadQuery({ loadId });
  const {
    poNumber,
    status,
    driver = {},
    trailer = {},
    location = {},
    createdDate,
    lastUpdatedDate,
    deliveryStartDateTime,
    deliveryEndDateTime,
    compartments = {},
    fillAll = false,
    processingHistory = [],
    billOfLadingIds = [],
  } = loadQuery;

  // driverHas :: String -> Bool
  const driverHas = R.has(R.__, driver);

  const isCompleted = R.equals(status, 'COMPLETED');

  // prettier-ignore
  const formattedCreatedDate = R.isNil(createdDate) 
    ? '--' 
    : formatDate(new Date(createdDate));
  const formattedLastUpdatedDate = R.isNil(lastUpdatedDate)
    ? '--'
    : formatDate(new Date(lastUpdatedDate));
  const formattedDeliveryStartDateTime = R.isNil(deliveryStartDateTime)
    ? '--'
    : formatDateTime(new Date(deliveryStartDateTime));
  const formattedDeliveryEndDateTime = R.isNil(deliveryEndDateTime)
    ? '--'
    : formatDateTime(new Date(deliveryEndDateTime));
  const formattedDriver = R.and(driverHas('firstName'), driverHas('lastName'))
    ? `${driver.lastName}, ${driver.firstName}`
    : '--';

  const handleDelete = () => {
    onDelete({ driver, load: loadQuery });
  };

  const handleDownloadBol = () => {
    downloadFile(billOfLadingIds, location.locationId, carrier);
  };

  const destinationCount = Object.values(compartments).filter(
    ({ destination }) => !R.isNil(destination)
  ).length;

  const buttons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      {status === 'COMPLETED' ? (
        <>
          {isDispatchingManage && (
            <Button onClick={() => onDuplicate(loadId)} sx={{ width: 150 }} variant="outlined">
              Duplicate
            </Button>
          )}
          <Button onClick={onClose} sx={{ width: 150 }}>
            Close
          </Button>
        </>
      ) : isDispatchingManage ? (
        <>
          <Button onClick={handleDelete} sx={{ width: 150 }} variant="outlined">
            Delete
          </Button>
          <Button onClick={() => onEdit(loadId)} sx={{ width: 150 }}>
            Edit
          </Button>
        </>
      ) : (
        <Button onClick={onClose} sx={{ width: 150 }}>
          Close
        </Button>
      )}
    </Box>
  );
  return (
    <Drawer anchor="right" onClose={onClose} open>
      <DrawerLayoutWrapper
        buttons={buttons}
        headerButtonText="CLOSE"
        isLoading={isFetching}
        onHeaderButtonClick={onClose}
        title="View Load"
      >
        <Stack sx={{ p: 3, width: 390 }}>
          <Item
            label="Internal PO Number"
            value={
              <Stack>
                {R.or(poNumber, '--')}
                <StatusChip status={status} />
              </Stack>
            }
          />
          <Item label="Driver" value={formattedDriver} />
          <Item label="Trailer ID" value={propOrDashes('vaporTightnessCode', trailer)} />
          <Item
            label="Terminal Location"
            value={location.name ? `${location.locationId} ${location.name.toUpperCase()}` : '--'}
          />

          <Stack direction="row" justifyContent="space-between">
            <Box>
              <Item label="Date Created" value={formattedCreatedDate} />
            </Box>
            <Box>
              <Item label="Last Edited" value={formattedLastUpdatedDate} />
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Box width={87}>
              <Item label="Delivery Start" value={formattedDeliveryStartDateTime} />
            </Box>
            <Box width={87}>
              <Item label="Delivery End" value={formattedDeliveryEndDateTime} />
            </Box>
          </Stack>
        </Stack>
        {isSuccess && !!destinationCount && (
          <Accordion sx={accordionStyles}>
            <AccordionSummary count={destinationCount} label="Destinations" />
            {Object.values(compartments)
              .filter(({ destination }) => !R.isNil(destination))
              .map(c => (
                <Fragment key={c.compartmentId}>
                  <Stack direction="row" justifyContent="space-between" sx={{ px: 3, width: 390 }}>
                    <Box width={162}>
                      <Item label="Destination ID" value={c.destination.destinationId} />
                    </Box>
                    <Box width={120}>
                      <Item label="Destination Name" value={c.destination.name} />
                    </Box>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ px: 3, width: 390 }}>
                    <Box>
                      <Item
                        label="Address"
                        value={
                          <>
                            {c.destination.street ? (
                              <>
                                {c.destination.street} <br />
                              </>
                            ) : null}
                            {c.destination.city}, {c.destination.state}
                          </>
                        }
                      />
                    </Box>
                  </Stack>
                </Fragment>
              ))}
          </Accordion>
        )}
        {Object.values(compartments).map(compartment => (
          <Accordion key={compartment.compartmentId} sx={accordionStyles}>
            <AccordionSummary
              label={fillAll ? 'Compartment Information' : `Compartment # ${compartment.position}`}
            />
            {R.cond([
              [R.pathEq(['loadType', 'loadTypeId'], 1), c => <CustomRecipe compartment={c} />],
              [R.pathEq(['loadType', 'loadTypeId'], 2), c => <HouseRecipe compartment={c} />],
              [R.pathEq(['loadType', 'loadTypeId'], 3), c => <Unload compartment={c} />],
              [R.T, R.always(<EmptyLoad />)],
            ])(compartment)}
          </Accordion>
        ))}

        {R.gt(processingHistory.length, 0) && (
          <Accordion sx={accordionStyles}>
            <AccordionSummary count={processingHistory.length} label="Load Updates" />
            <Box px={3}>
              {processingHistory.map(({ message, lastUpdated }) => (
                <Item
                  key={lastUpdated}
                  label={message}
                  value={format('PPPppp', new Date(lastUpdated))}
                />
              ))}
            </Box>
          </Accordion>
        )}

        {isCompleted && (
          <Stack sx={{ p: 3, width: 390 }}>
            <ListItem
              disableGutters
              secondaryAction={
                <IconButton edge="end" onClick={handleDownloadBol}>
                  <DownloadIcon color="primary" />
                </IconButton>
              }
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <DescriptionIcon />
              </ListItemIcon>
              <ListItemText primary="bol.csv" />
            </ListItem>
          </Stack>
        )}
      </DrawerLayoutWrapper>
    </Drawer>
  );
};

ViewLoadDrawer.propTypes = {
  loadId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default ViewLoadDrawer;
